import { useEffect, useRef, useState } from 'react'
import { Icon } from '../icon/Icon'
import { useI18n } from '@/lib/i18n'
import { useUIStore } from '@/stores/useUIStore'
import { useChatStore } from '@/stores/chatStore'
import { useProcessesStore } from '@/stores/processesStore'
import { useSubagentRunsStore } from '@/stores/subagentRunsStore'
import { notifyMessage } from '@/hooks/useNotification'
import { synthApi } from '@/lib/api/synthApi'
import { moveSessionToBackground } from '@/lib/backgroundDetach'
import { MarkdownContent } from './MarkdownContent'
import { ReasoningBlock } from './ReasoningBlock'
import { ToolCallPart } from './ToolCallPart'
import { useRetryCountdown } from './RetryStatus'
import { isRecoverableErrorType } from '@/lib/streamErrors'
import type { ReasoningBlockData, IntermediateTextBlockData } from '@/stores/chatStore'

export interface SubagentToolCall {
  name: string
  args: Record<string, unknown>
  id?: string
  result?: { content: string; isError?: boolean }
  started_at?: string
  completed_at?: string
  iteration: number
}

/** Ошибка саб-агента из tool_result metadata (B7): плашка внизу блока. */
export interface SubagentErrorInfo {
  isError?: boolean
  attempts?: number
  maxRetries?: number
  errorType?: string
  error?: string
}

export interface SubagentPartData {
  name: string
  prompt?: string
  response?: string
  sessionId?: string
  /** call_id тула `task` — связывает блок с событиями subagent.retry. */
  callId?: string
  /** true для live-блока (идёт стрим) — retry-статус актуален только в нём. */
  live?: boolean
  /**
   * ADR-111 §detach: прогон саб-агента ещё идёт — в шапке доступна кнопка
   * «В фон». Для исторических (завершённых) блоков не выставляется.
   */
  running?: boolean
  errorInfo?: SubagentErrorInfo
  reasoning_blocks?: Array<{ text: string; iteration: number }>
  intermediate_text_blocks?: Array<{ text: string; iteration: number }>
  tool_calls?: SubagentToolCall[]
  steps?: number
  description?: string
  /**
   * ADR-111: карточка **фонового** прогона (`task` с `background: true` или
   * переведённого в фон). У него в `result.metadata` нет ни шагов, ни
   * `tool_calls` — публикация в родительский стрим отключена, а `tool_result`
   * возвращается сразу. Шаги лежат в **истории дочерней сессии** — UI
   * догружает их лениво (`stores/subagentRunsStore`).
   */
  background?: boolean
}

export function SubagentPart({ subagent }: { subagent: SubagentPartData }) {
  const { t } = useI18n()
  const [expanded, setExpanded] = useState(false)
  // ADR-111 §detach: запрос перевода в фон — в полёте / уже переведён.
  const [detaching, setDetaching] = useState(false)
  const [detached, setDetached] = useState(false)

  // Состояния retry/resume привязаны к сессии открытой вкладки (родитель или ребёнок).
  const activeTab = useUIStore((s) => s.centerTabs.find((tb) => tb.id === s.activeCenterTabId))
  const sid = (activeTab?.data?.sessionId as string) || ''
  const subagentRetry = useChatStore((s) => s.subagentRetryBySession[sid] || null)
  const resuming = useChatStore((s) => s.subagentResumingBySession[sid] || null)

  /**
   * ADR-111: фоновая карточка. Шагов в `result.metadata` нет (публикация в
   * родительский стрим отключена) — они лежат в истории дочерней сессии.
   * Загрузка ленивая: по раскрытию карточки + по терминальному статусу
   * прогона (`processesStore`).
   */
  const childSessionId = subagent.background ? subagent.sessionId : undefined
  const runEntry = useSubagentRunsStore((s) => (childSessionId ? s.byChild[childSessionId] : undefined))
  const runStatus = useProcessesStore((s) => {
    if (!childSessionId) return undefined
    const run = s.order
      .map((id) => s.byId[id])
      .find((r) => r?.kind === 'subagent' && r.childSessionId === childSessionId)
    return run?.status
  })

  // Загрузка по раскрытию карточки (основной путь).
  useEffect(() => {
    if (!expanded || !childSessionId) return
    void useSubagentRunsStore.getState().ensure(childSessionId)
  }, [expanded, childSessionId])

  // Прогон стал терминальным → история дочерней сессии полна: перечитываем её.
  const wasTerminal = useRef(false)
  useEffect(() => {
    if (!runStatus) return
    const terminal = runStatus !== 'running'
    if (terminal && !wasTerminal.current && childSessionId) {
      void useSubagentRunsStore.getState().invalidate(childSessionId)
      void useSubagentRunsStore.getState().ensure(childSessionId)
    }
    wasTerminal.current = terminal
  }, [runStatus, childSessionId])

  const retryVisible = subagent.live === true
    && subagentRetry != null
    && (!subagentRetry.callId || subagentRetry.callId === subagent.callId)
  const retrySeconds = useRetryCountdown(subagentRetry?.retryInMs ?? 5000)

  const resumeActive = resuming != null
    && !!subagent.sessionId
    && resuming.childSessionId === subagent.sessionId

  const showErrorBar = !resumeActive
    && !!subagent.errorInfo?.isError
    && !!subagent.sessionId

  const handleResume = () => {
    if (!subagent.sessionId) return
    synthApi.resumeSubagent(subagent.sessionId).catch((e) => {
      notifyMessage({ text: `${t('chat.subagent.resumeFailed')}: ${(e as Error).message}`, type: 'error', category: 'error', sessionId: subagent.sessionId })
    })
  }

  const handleStopResume = () => {
    if (!subagent.sessionId) return
    synthApi.stopGeneration(subagent.sessionId).catch(() => {})
  }

  const handleRetrySteps = () => {
    if (!childSessionId) return
    void useSubagentRunsStore.getState().invalidate(childSessionId)
    void useSubagentRunsStore.getState().ensure(childSessionId)
  }

  /**
   * Шаги карточки: у фоновой — из догруженной истории ребёнка, у обычной —
   * из `result.metadata`/live-данных. Приоритет живых данных: если они есть,
   * стор их не перекрывает.
   */
  const liveToolCalls = subagent.tool_calls || []
  const storeData = runEntry?.status === 'ready' ? runEntry.data : undefined
  const toolCalls: SubagentToolCall[] = liveToolCalls.length > 0
    ? liveToolCalls
    : (storeData?.toolCalls ?? [])
  const reasoningBlocks: ReasoningBlockData[] = (subagent.reasoning_blocks?.length ?? 0) > 0
    ? subagent.reasoning_blocks!
    : (storeData?.reasoningBlocks ?? [])
  const intermediateTextBlocks: IntermediateTextBlockData[] = (subagent.intermediate_text_blocks?.length ?? 0) > 0
    ? subagent.intermediate_text_blocks!
    : (storeData?.intermediateTextBlocks ?? [])
  const steps = subagent.steps ?? storeData?.steps
  const stepsLoading = childSessionId != null && runEntry?.status === 'loading'
  const stepsError = childSessionId != null && runEntry?.status === 'error' ? runEntry.error : undefined

  const maxIter = Math.max(
    0,
    ...reasoningBlocks.map((r) => r.iteration),
    ...intermediateTextBlocks.map((it) => it.iteration),
    ...toolCalls.map((tc) => tc.iteration),
  )

  const items: Array<
    | { type: 'reasoning'; data: ReasoningBlockData }
    | { type: 'intermediate_text'; data: IntermediateTextBlockData }
    | { type: 'tool_calls'; calls: typeof toolCalls }
  > = []

  for (let iter = 0; iter <= maxIter; iter++) {
    for (const r of reasoningBlocks) {
      if (r.iteration === iter) items.push({ type: 'reasoning', data: r })
    }
    for (const it of intermediateTextBlocks) {
      if (it.iteration === iter) items.push({ type: 'intermediate_text', data: it })
    }
    const iterCalls = toolCalls.filter((tc) => tc.iteration === iter)
    if (iterCalls.length > 0) {
      items.push({ type: 'tool_calls', calls: iterCalls })
    }
  }

  const shortSessionId = subagent.sessionId?.slice(0, 8)

  // Enter/Space на заголовке-контейнере переключают раскрытие (role="button").
  const handleHeaderKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      setExpanded((v) => !v)
    }
  }

  const handleOpenSession = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!subagent.sessionId) return
    useUIStore.getState().openTab({
      id: `chat-${subagent.sessionId}`,
      type: 'chat',
      title: `${t('chat.subagent.title')}: ${subagent.name}`,
      data: { sessionId: subagent.sessionId },
    })
  }

  /**
   * ADR-111 §detach: перевести идущий прогон в фон. Запрос — к сессии открытого
   * чата (владельцу блокирующего вызова `task`), а не к дочерней сессии саб-агента:
   * переводится именно текущий ход родителя.
   */
  const handleBackground = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!sid || detaching || detached) return
    setDetaching(true)
    void moveSessionToBackground(sid, {
      type: 'subagent',
      callIds: subagent.callId ? [subagent.callId] : undefined,
    })
      .then((response) => {
        if (response) setDetached(true)
      })
      .finally(() => setDetaching(false))
  }

  // Кнопка «В фон» — только у идущего прогона и только когда известна сессия-владелец.
  const canDetach = subagent.running === true && !!sid && !detached

  const errorRecoverable = isRecoverableErrorType(subagent.errorInfo?.errorType)
  const errorAttemptsLabel = subagent.errorInfo?.attempts != null
    ? t('chat.subagent.errorAttempts', {
        attempts: String(subagent.errorInfo.attempts),
        max: String(subagent.errorInfo.maxRetries ?? 3),
      })
    : null

  return (
    <div data-card="service" className="mb-2 rounded-md border border-border bg-muted overflow-hidden">
      <div
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        className="flex w-full items-center gap-1.5 px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
        onClick={() => setExpanded(!expanded)}
        onKeyDown={handleHeaderKeyDown}
      >
        <Icon
          name={expanded ? 'arrow-down-s' : 'arrow-right-s'}
          className="size-3.5 transition-transform"
        />
        <Icon name="git-branch" className="size-3.5 text-purple-400" />
        <span className="font-medium">{t('chat.subagent.title')}: {subagent.name}</span>
        {shortSessionId && (
          <span className="font-mono text-[0.625rem] text-muted-foreground/70">
            {shortSessionId}
          </span>
        )}
        {(canDetach || subagent.sessionId) && (
          <span className="ml-auto inline-flex items-center gap-1">
            {canDetach && (
              <button
                type="button"
                disabled={detaching}
                className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[0.625rem] text-purple-400 hover:text-purple-300 hover:bg-purple-500/10 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-default disabled:hover:bg-transparent"
                onClick={handleBackground}
                title={t('chat.background.hint')}
                aria-label={t('chat.background.move')}
              >
                <Icon name="download" className="size-3" />
                {t('chat.background.move')}
              </button>
            )}
            {subagent.sessionId && (
              <button
                type="button"
                className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[0.625rem] text-purple-400 hover:text-purple-300 hover:bg-purple-500/10 transition-colors cursor-pointer"
                onClick={handleOpenSession}
                title={subagent.sessionId}
              >
                <Icon name="external-link" className="size-3" />
                {t('chat.subagent.open')}
              </button>
            )}
          </span>
        )}
        {steps != null && (
          <span className="text-[0.625rem] text-muted-foreground/60">
            {steps} {t('chat.subagent.steps')}
          </span>
        )}
      </div>
      {expanded && (
        <div className="p-3 border-t border-border space-y-2">
          {subagent.prompt && (
            <div>
              <div className="text-[0.625rem] text-muted-foreground/60 mb-1 uppercase tracking-wide">Prompt</div>
              <MarkdownContent content={subagent.prompt} className="text-xs" />
            </div>
          )}

          {stepsLoading && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Icon name="loader-2" className="size-3 animate-spin" />
              {t('chat.subagent.stepsLoading')}
            </div>
          )}

          {stepsError && (
            <div className="flex items-center gap-1.5 text-xs text-destructive">
              <Icon name="error-warning" className="size-3 shrink-0" />
              <span className="flex-1 min-w-0">{t('chat.subagent.stepsFailed')}</span>
              <button
                type="button"
                onClick={handleRetrySteps}
                className="shrink-0 rounded px-2 py-0.5 text-[0.6875rem] font-medium bg-destructive/15 text-destructive hover:bg-destructive/25 transition-colors"
              >
                {t('chat.subagent.stepsRetry')}
              </button>
            </div>
          )}

          {items.map((item, i) => {
            if (item.type === 'reasoning') {
              return <ReasoningBlock key={`r${i}`} reasoning={item.data.text} />
            }
            if (item.type === 'intermediate_text') {
              return (
                <div key={`it${i}`} className="my-2 text-sm">
                  <MarkdownContent content={item.data.text} />
                </div>
              )
            }
            if (item.type === 'tool_calls') {
              return item.calls.map((call, j) => (
                <ToolCallPart
                  key={call.id || j}
                  toolCall={{
                    name: call.name,
                    args: call.args,
                    id: call.id,
                    callId: call.id,
                    status: 'done',
                    result: call.result,
                    startedAt: call.started_at,
                    completedAt: call.completed_at,
                    iteration: call.iteration,
                  }}
                />
              ))
            }
            return null
          })}

          {subagent.response && (
            <div>
              <div className="text-[0.625rem] text-muted-foreground/60 mb-1 uppercase tracking-wide">Response</div>
              <MarkdownContent content={subagent.response} className="text-xs" />
            </div>
          )}
        </div>
      )}

      {/* Внизу блока: статус ретрая / возобновление / ошибка. Видны и у свёрнутого блока. */}
      {retryVisible && (
        <div className="flex items-center gap-1.5 px-3 py-1.5 border-t border-status-warning/20 bg-status-warning/5">
          <Icon name="refresh" className="size-3.5 text-status-warning shrink-0" />
          <span className="text-xs text-status-warning">
            {t('chat.retry.inProgress', {
              current: String(subagentRetry?.attempt ?? 1),
              max: String(subagentRetry?.maxRetries ?? 3),
              sec: String(retrySeconds),
            })}
          </span>
        </div>
      )}

      {resumeActive && (
        <div className="flex items-center gap-1.5 px-3 py-1.5 border-t border-purple-500/20 bg-purple-500/5">
          <Icon name="refresh" className="size-3.5 text-purple-500 shrink-0 animate-spin" />
          <span className="text-xs text-purple-600 dark:text-purple-400 flex-1">
            {t('chat.subagent.resuming')}
          </span>
          <button
            onClick={handleStopResume}
            className="rounded px-2 py-0.5 text-[0.6875rem] text-muted-foreground hover:text-foreground hover:bg-interactive-hover transition-colors"
          >
            {t('chat.subagent.stop')}
          </button>
        </div>
      )}

      {showErrorBar && (
        <div className="flex items-center gap-1.5 px-3 py-1.5 border-t border-destructive/20 bg-destructive/5">
          <Icon name="error-warning" className="size-3.5 text-destructive shrink-0" />
          <span className="text-xs text-destructive flex-1 min-w-0">
            {t('chat.subagent.errorStopped')}
            {errorAttemptsLabel && (
              <span className="ml-1 text-[0.625rem] text-muted-foreground">({errorAttemptsLabel})</span>
            )}
          </span>
          {errorRecoverable && (
            <button
              onClick={handleResume}
              className="shrink-0 rounded px-2 py-0.5 text-[0.6875rem] font-medium bg-destructive/15 text-destructive hover:bg-destructive/25 transition-colors"
            >
              {t('chat.subagent.resume')}
            </button>
          )}
        </div>
      )}
    </div>
  )
}
